import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import QRCode from 'qrcode';
import { dynamicImpactIncubatorsContent } from '../content/educational/dynamicImpactIncubatorsContent';

const generatePDF = async (content, title, keywords) => {
  try {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 25;
    const contentWidth = pageWidth - (margin * 2);
    const headerHeight = 40;
    const footerHeight = 20;
    const lineSpacing = 3;
    const paragraphSpacing = 1;
    const bulletIndent = 12;

    // Generate QR Codes
    const qrCodeHomepage = await QRCode.toDataURL('https://causeandimpact.org');
    const qrCodeApply = await QRCode.toDataURL('https://causeandimpact.org/apply');

    // Helper function to process markdown text
    const processMarkdown = (text) => {
      let processedText = text;
      let segments = [];
      
      // Split text into segments based on bold markers
      let lastIndex = 0;
      let boldRegex = /\*\*(.*?)\*\*/g;
      let match;
      
      while ((match = boldRegex.exec(processedText)) !== null) {
        if (match.index > lastIndex) {
          segments.push({
            text: processedText.substring(lastIndex, match.index),
            bold: false
          });
        }
        segments.push({
          text: match[1],
          bold: true
        });
        lastIndex = match.index + match[0].length;
      }
      
      if (lastIndex < processedText.length) {
        segments.push({
          text: processedText.substring(lastIndex),
          bold: false
        });
      }
      
      return segments;
    };

    // Helper function to handle bold keywords
    const processKeywords = (text, keywords) => {
      let processedText = text;
      keywords.forEach(keyword => {
        const regex = new RegExp(`(${keyword})`, 'gi');
        processedText = processedText.replace(regex, '**$1**');
      });
      return processedText;
    };

    // Helper function to handle bullet points and numbered lists
    const processList = (text, yPosition) => {
      const lines = text.split('\n');
      let currentY = yPosition;
      const numberWidth = 8;
      
      const renderTextWithBold = (text, x, y) => {
        const segments = processMarkdown(text);
        let currentX = x;
        
        segments.forEach(segment => {
          doc.setFont('helvetica', segment.bold ? 'bold' : 'normal');
          doc.text(segment.text, currentX, y);
          currentX += doc.getTextWidth(segment.text);
        });
        doc.setFont('helvetica', 'normal');
      };

      lines.forEach(line => {
        if (line.trim().startsWith('- ')) {
          const bulletText = line.substring(2);
          doc.text('•', margin + bulletIndent, currentY);
          const processedBullet = processKeywords(bulletText, keywords);
          const bulletLines = doc.splitTextToSize(processedBullet, contentWidth - (bulletIndent + 8));
          
          bulletLines.forEach((bLine) => {
            if (currentY + 4 > pageHeight - footerHeight) {
              doc.addPage();
              addHeader();
              addFooter(doc.internal.getNumberOfPages());
              currentY = headerHeight + 12;
            }
            renderTextWithBold(bLine, margin + bulletIndent + 6, currentY);
            currentY += 4;
          });
          currentY += 1;
        } else if (line.match(/^\d+\./)) {
          const numberPart = line.substring(0, line.indexOf('.') + 1);
          const textPart = line.substring(line.indexOf('.') + 1).trim();
          const processedNumber = processKeywords(textPart, keywords);
          const numberLines = doc.splitTextToSize(processedNumber, contentWidth - (bulletIndent + numberWidth + 8));
          
          numberLines.forEach((nLine, index) => {
            if (index === 0) {
              doc.text(numberPart, margin + bulletIndent, currentY);
              renderTextWithBold(nLine, margin + bulletIndent + numberWidth + 2, currentY);
            } else {
              renderTextWithBold(nLine, margin + bulletIndent + numberWidth + 2, currentY);
            }
            currentY += 4;
          });
          currentY += 1;
        } else {
          const processedLine = processKeywords(line, keywords);
          const textLines = doc.splitTextToSize(processedLine, contentWidth);
          
          textLines.forEach((tLine) => {
            renderTextWithBold(tLine, margin, currentY);
            currentY += 4;
          });
          currentY += 1;
        }
      });
      
      return currentY;
    };

    const addHeader = () => {
      doc.setFillColor(17, 24, 39);
      doc.rect(0, 0, pageWidth, headerHeight, 'F');

      // Gradient title effect
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(24);
      
      // Blue shadow
      doc.setTextColor(37, 99, 235);
      doc.text('Cause & Impact, Inc.', (pageWidth / 2) + 0.5, 20.1, { align: 'center' });
      
      // Green overlay
      doc.setTextColor(5, 150, 105);
      doc.text('Cause & Impact, Inc.', pageWidth / 2, 20, { align: 'center' });

      doc.setFontSize(10);
      doc.setTextColor(255, 255, 255);
      doc.text('Starting a Nonprofit Cause never got easier!', pageWidth / 2, 30, { align: 'center' });

      doc.addImage(qrCodeHomepage, 'PNG', pageWidth - margin - 25, 10, 20, 20);
    };

    const addFooter = (pageNumber) => {
      doc.setFillColor(17, 24, 39);
      doc.rect(0, pageHeight - footerHeight, pageWidth, footerHeight, 'F');

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(255, 255, 255);
      doc.text(
        'Cause & Impact, Inc. | EIN: 84-3918881 | www.causeandimpact.org',
        pageWidth / 2,
        pageHeight - 8,
        { align: 'center' }
      );
      doc.text(`Page ${pageNumber}`, pageWidth - margin, pageHeight - 8, { align: 'right' });
    };

    const addCallToAction = (yPosition) => {
      const ctaHeight = 45;
      const qrSize = 30;
      doc.setFillColor(37, 99, 235);
      doc.roundedRect(margin, yPosition, contentWidth, ctaHeight, 5, 5, 'F');

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(20);
      doc.setTextColor(255, 255, 255);
      doc.text('Ready to Get Started?', pageWidth / 2, yPosition + 20, { align: 'center' });

      doc.setFontSize(12);
      doc.text('Visit causeandimpact.org/apply', pageWidth / 2, yPosition + 30, { align: 'center' });

      doc.addImage(qrCodeApply, 'PNG', pageWidth - qrSize - margin - 10, yPosition + 7.5, qrSize, qrSize);
    };

    const addContent = () => {
      let yPosition = headerHeight + 12;
      let pageNumber = 1;

      // Add title with optimized styling
      doc.setFontSize(20);
      doc.setTextColor(37, 99, 235);
      doc.setFont('helvetica', 'bold');
      const titleLines = doc.splitTextToSize(title, contentWidth);
      doc.text(titleLines, pageWidth / 2, yPosition + 6, { align: 'center' });
      yPosition += (titleLines.length * 7) + 12;

      content.forEach((section, index) => {
        if (yPosition + 35 > pageHeight - footerHeight - 45) {
          doc.addPage();
          pageNumber++;
          addHeader();
          addFooter(pageNumber);
          yPosition = headerHeight + 12;
        }

        // Section Heading
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        doc.setTextColor(5, 150, 105);
        doc.text(section.heading, margin, yPosition);
        yPosition += 6;

        // Process section content
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.setTextColor(31, 41, 55);
        
        const paragraphs = section.text.split('\n\n');
        paragraphs.forEach(paragraph => {
          yPosition = processList(paragraph, yPosition);
          yPosition += paragraphSpacing;
        });

        if (index === content.length - 1) {
          addCallToAction(pageHeight - footerHeight - 38);
        }
      });
    };

    // Generate PDF
    addHeader();
    addFooter(1);
    addContent();

    return doc;
  } catch (error) {
    console.error('Error generating PDF:', error);
    return null;
  }
};

export const generateDynamicImpactOverview = async () => {
  return generatePDF(
    dynamicImpactIncubatorsContent.sections,
    dynamicImpactIncubatorsContent.title,
    dynamicImpactIncubatorsContent.keywords
  );
}; 